import { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import Logo from "@/components/Logo";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Compass, ArrowRight, ArrowLeft } from "lucide-react";

// The 5 personalization questions. Order matters; do not reorder without also
// updating the matching backend fields in /api/students/{id}/interests.
const QUESTIONS = [
  {
    field: "interest_activity",
    label: "What is your favorite sport or physical activity?",
    placeholder: "Football, swimming, dance, hiking — what gets you moving?",
  },
  {
    field: "interest_hobby",
    label: "What do you like to do for fun outside of school?",
    placeholder: "Video games, drawing, cooking, building things — what do you love to do?",
  },
  {
    field: "interest_subject",
    label: "What is your favorite subject in school?",
    placeholder: "Math, science, history, art — what class do you look forward to most?",
  },
  {
    field: "interest_career",
    label: "What do you want to be when you grow up?",
    placeholder: "Dream big! Astronaut, chef, teacher, athlete — anything goes!",
  },
  {
    field: "interest_explore",
    label: "Where in the world would you most love to explore?",
    placeholder: "Outer space, the ocean, ancient ruins, a big city — where would you go?",
  },
];

const TOTAL = QUESTIONS.length;

const OnboardingQuiz = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [climber, setClimber] = useState(null);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState({});
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    api.get(`/students/${id}`).then((r) => {
      if (cancelled) return;
      setClimber(r.data);
      // Pre-fill any answers already on file (in case they re-enter from settings)
      setAnswers({
        interest_activity: r.data.interest_activity || "",
        interest_hobby: r.data.interest_hobby || "",
        interest_subject: r.data.interest_subject || "",
        interest_career: r.data.interest_career || "",
        interest_explore: r.data.interest_explore || "",
      });
    }).catch(() => {
      toast.error("Could not load climber");
      navigate("/dashboard");
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [id, user, navigate]);

  const current = QUESTIONS[step];
  const currentValue = answers[current?.field] || "";
  const isLast = step === TOTAL - 1;
  const progressPct = useMemo(() => Math.round(((step + 1) / TOTAL) * 100), [step]);

  if (authLoading || loading) {
    return <div className="min-h-screen flex items-center justify-center text-mm-navy/60 bg-mm-parchment bg-topo">Loading…</div>;
  }
  if (!user) return <Navigate to="/login" replace />;

  const next = (e) => {
    e?.preventDefault?.();
    if (isLast) submit();
    else setStep((s) => Math.min(TOTAL - 1, s + 1));
  };

  const back = () => setStep((s) => Math.max(0, s - 1));

  const submit = async () => {
    setSubmitting(true);
    try {
      // Only send non-empty fields; the PATCH endpoint preserves omitted ones.
      const payload = {};
      for (const q of QUESTIONS) {
        const v = (answers[q.field] || "").trim();
        if (v) payload[q.field] = v;
      }
      if (Object.keys(payload).length > 0) {
        await api.patch(`/students/${id}/interests`, payload);
        toast.success(`${climber?.name || "Climber"} is all set!`);
      } else {
        toast.info("Skipped — you can add interests anytime from Climbers.");
      }
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save your answers");
    } finally {
      setSubmitting(false);
    }
  };

  const skipAll = () => {
    if (submitting) return;
    toast.info("No problem — you can add interests later from the Climbers page.");
    navigate("/dashboard");
  };

  return (
    <div className="min-h-screen bg-mm-parchment bg-topo flex flex-col" data-testid="onboarding-quiz-page">
      <div className="container mx-auto py-6">
        <Logo size={48} />
      </div>

      <div className="flex-1 flex items-center justify-center px-4 py-6">
        <div className="w-full max-w-2xl bg-white border border-mm-navy/10 rounded-3xl p-8 sm:p-12 fade-up shadow-sm">
          {/* Intro — only on step 1 (the first time) */}
          {step === 0 && (
            <div className="mb-8" data-testid="onboarding-intro">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-mm-gold/25 text-mm-navy flex items-center justify-center">
                  <Compass size={24} strokeWidth={2.5} />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.22em] font-bold text-mm-steel">Trail briefing</div>
                  <h1 className="font-heading text-3xl sm:text-4xl font-black text-mm-navy">
                    Welcome, Explorer!
                  </h1>
                </div>
              </div>
              <div className="mt-5 space-y-3 text-mm-navy/85 text-[15px] leading-relaxed">
                <p>Before we chart your course, let's get to know you.</p>
                <p>
                  Every great expedition is personal. The more we know about what excites you,
                  the better we can tailor your learning adventures to feel like they were made just for you.
                </p>
                <p>
                  Answer these 5 quick questions and we will use your answers to make your expeditions
                  feel familiar, relevant, and a whole lot more fun.
                </p>
                <p>
                  There are no right or wrong answers — just be honest about what you love!
                  Stick to general interests — no need to share specific names, locations, or personal details.
                </p>
                <p className="text-mm-navy/70 text-sm">
                  You can update your answers anytime from your profile settings.
                </p>
              </div>
            </div>
          )}

          {/* Progress */}
          <div className="flex items-center justify-between text-xs uppercase tracking-widest font-bold text-mm-steel">
            <span data-testid="onboarding-progress-label">Question {step + 1} of {TOTAL}</span>
            <span>{progressPct}%</span>
          </div>
          <div className="mt-2 h-2 rounded-full bg-mm-navy/10 overflow-hidden">
            <div
              className="h-full bg-mm-gold transition-all duration-300"
              style={{ width: `${progressPct}%` }}
              data-testid="onboarding-progress-bar"
              aria-valuenow={progressPct}
              role="progressbar"
            />
          </div>

          {/* Question */}
          <form onSubmit={next} className="mt-7">
            <h2 className="font-heading text-2xl sm:text-3xl font-black text-mm-navy leading-tight" data-testid="onboarding-question-label">
              {current.label}
            </h2>
            <input
              key={current.field}
              type="text"
              autoFocus
              value={currentValue}
              onChange={(e) => setAnswers({ ...answers, [current.field]: e.target.value })}
              placeholder={current.placeholder}
              maxLength={140}
              className="mt-5 w-full text-lg bg-white border-2 border-mm-navy/15 rounded-xl px-5 py-4 focus:outline-none focus:border-mm-steel focus:ring-2 focus:ring-mm-steel/30 placeholder:text-mm-navy/40"
              data-testid={`onboarding-input-${current.field}`}
            />

            <div className="mt-7 flex items-center justify-between gap-4">
              <button
                type="button"
                onClick={back}
                disabled={step === 0 || submitting}
                className="btn-trail btn-outline-navy text-sm disabled:opacity-30"
                data-testid="onboarding-back-btn"
              >
                <ArrowLeft size={14} strokeWidth={2.5} /> Back
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="btn-trail btn-gold text-sm"
                data-testid="onboarding-next-btn"
              >
                {isLast ? (submitting ? "Saving…" : "Finish & start climbing") : <>Next <ArrowRight size={14} strokeWidth={2.5} /></>}
              </button>
            </div>
          </form>

          <div className="mt-8 pt-6 border-t border-mm-navy/10 text-center">
            <button
              type="button"
              onClick={skipAll}
              disabled={submitting}
              className="text-sm text-mm-navy/60 hover:text-mm-navy font-bold underline-offset-4 hover:underline"
              data-testid="onboarding-skip-btn"
            >
              Skip for now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingQuiz;
